import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { 
  Container, 
  Typography, 
  Button, 
  Paper, 
  Alert, 
  CircularProgress, 
  Stack,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DeliveryRule } from '../services/deliveryConfigService';
import { getDeliveryRules, updateDeliveryRules } from '../services/deliveryRulesService';
import { driverService } from '../services/driverService';
import Layout from '../components/Layout';
import ScopeRequirementAlert from '../components/ScopeRequirementAlert';
import { useAdminScope } from '../contexts/AdminScopeContext';
import { describeActiveScope } from '../utils/adminScopeUtils';

type DeliveryRulesSettingsPanelProps = {
  embedded?: boolean;
};

export const DeliveryRulesSettingsPanel: React.FC<DeliveryRulesSettingsPanelProps> = ({
  embedded = false,
}) => {
  const {
    activeScopeType,
    selectedGroupId,
    selectedStore,
    stores,
  } = useAdminScope();
  const [rules, setRules] = useState<DeliveryRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [maxClientDistanceKm, setMaxClientDistanceKm] = useState<number | undefined>(undefined);
  const [enableAutoFreeDelivery, setEnableAutoFreeDelivery] = useState<boolean>(false);
  const [freeDeliveryMinOrderAmount, setFreeDeliveryMinOrderAmount] = useState<number | undefined>(undefined);
  const [maxFreeDeliveryKm, setMaxFreeDeliveryKm] = useState<number | undefined>(undefined);
  const [globalPercentage, setGlobalPercentage] = useState<number>(100);
  const [applyingPercentage, setApplyingPercentage] = useState(false);
  const [percentageSuccess, setPercentageSuccess] = useState<string | null>(null);
  const [percentageError, setPercentageError] = useState<string | null>(null);
  const scopeSummary = useMemo(
    () =>
      describeActiveScope({
        activeScopeType,
        selectedGroupId,
        selectedStoreId: selectedStore?.id || null,
        stores,
      }),
    [activeScopeType, selectedGroupId, selectedStore, stores]
  );
  const fetchRules = useCallback(async () => {
    if (!selectedGroupId) {
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const rulesConfig = await getDeliveryRules(selectedGroupId);
      setRules(rulesConfig.rules);
      setMaxClientDistanceKm(
        typeof rulesConfig.maxClientDistanceKm === 'number'
          ? rulesConfig.maxClientDistanceKm
          : undefined
      );
      setEnableAutoFreeDelivery(rulesConfig.enableAutoFreeDelivery === true);
      setFreeDeliveryMinOrderAmount(
        typeof rulesConfig.freeDeliveryMinOrderAmount === 'number'
          ? rulesConfig.freeDeliveryMinOrderAmount
          : undefined
      );
      setMaxFreeDeliveryKm(
        typeof rulesConfig.maxFreeDeliveryKm === 'number'
          ? rulesConfig.maxFreeDeliveryKm
          : undefined
      );
    } catch (err: any) {
      setError(`Échec de récupération des règles de livraison: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedGroupId]);

  useEffect(() => {
    if (!selectedGroupId) {
      setLoading(false);
      setRules([]);
      setError(null);
      setSuccess(null);
      return;
    }
    void fetchRules();
  }, [fetchRules, selectedGroupId]);

  // Gérer les changements dans les champs des règles
  const handleRuleChange = (index: number, field: keyof DeliveryRule, value: number) => {
    const updatedRules = [...rules];
    updatedRules[index] = {
      ...updatedRules[index],
      [field]: value
    };
    
    setRules(updatedRules);

    // Aligner automatiquement maxClientDistanceKm sur la règle la plus éloignée si nécessaire
    setMaxClientDistanceKm((current) => {
      if (!updatedRules || updatedRules.length === 0) return current;
      const highestMaxKm = updatedRules.reduce((acc, r) => {
        const v = typeof r.maxKm === 'number' ? r.maxKm : acc;
        return v > acc ? v : acc;
      }, 0);
      if (highestMaxKm <= 0) return current;
      if (typeof current !== 'number' || current < highestMaxKm) {
        return highestMaxKm;
      }
      return current;
    });
  };

  // Ajouter une nouvelle règle
  const addRule = () => {
    if (rules.length === 0) {
      // Si aucune règle n'existe, créer une règle par défaut
      const initialRules: DeliveryRule[] = [{ fee: 10, maxKm: 2, minKm: 0 }];
      setRules(initialRules);
      // Aligner aussi maxClientDistanceKm au premier maxKm si plus petit ou non défini
      setMaxClientDistanceKm((current) => {
        const highestMaxKm = 2;
        if (typeof current !== 'number' || current < highestMaxKm) {
          return highestMaxKm;
        }
        return current;
      });
      return;
    }

    const lastRule = rules[rules.length - 1];
    const newRule: DeliveryRule = {
      fee: lastRule ? lastRule.fee + 5 : 10,
      minKm: lastRule ? lastRule.maxKm + 0.01 : 0,
      maxKm: lastRule ? lastRule.maxKm + 5 : 5
    };

    const newRules = [...rules, newRule];
    setRules(newRules);

    // Aligner automatiquement maxClientDistanceKm sur la nouvelle règle si elle va plus loin
    setMaxClientDistanceKm((current) => {
      const highestMaxKm = newRules.reduce((acc, r) => {
        const v = typeof r.maxKm === 'number' ? r.maxKm : acc;
        return v > acc ? v : acc;
      }, 0);
      if (highestMaxKm <= 0) return current;
      if (typeof current !== 'number' || current < highestMaxKm) {
        return highestMaxKm;
      }
      return current;
    });
  };

  // Supprimer une règle
  const deleteRule = (index: number) => {
    if (rules.length <= 1) return; // Garder au moins une règle
    
    const updatedRules = [...rules];
    updatedRules.splice(index, 1);
    
    setRules(updatedRules);

    // Ne jamais baisser automatiquement maxClientDistanceKm, mais le remonter si besoin
    setMaxClientDistanceKm((current) => {
      if (!updatedRules || updatedRules.length === 0) return current;
      const highestMaxKm = updatedRules.reduce((acc, r) => {
        const v = typeof r.maxKm === 'number' ? r.maxKm : acc;
        return v > acc ? v : acc;
      }, 0);
      if (highestMaxKm <= 0) return current;
      if (typeof current !== 'number' || current < highestMaxKm) {
        return highestMaxKm;
      }
      return current;
    });
  };

  // Enregistrer les règles
  const handleSave = async () => {
    if (!selectedGroupId) {
      setError('Aucun groupe actif sélectionné.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      await updateDeliveryRules(
        selectedGroupId,
        { rules, maxClientDistanceKm, enableAutoFreeDelivery, freeDeliveryMinOrderAmount, maxFreeDeliveryKm }
      );
      setSuccess('Règles de livraison enregistrées avec succès.');
    } catch (err: any) {
      setError(`Échec d'enregistrement des règles de livraison: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    const loadingContent = (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress />
        <Typography>Chargement des règles de livraison...</Typography>
      </Stack>
    );

    return embedded ? (
      <Paper variant="outlined" sx={{ p: 2 }}>
        {loadingContent}
      </Paper>
    ) : (
      <Layout title="Tarifs de Livraison">
        <Container>{loadingContent}</Container>
      </Layout>
    );
  }

  const content = (
    <>
      {!embedded && (
        <Typography variant="h4" gutterBottom>
          Configuration des Tarifs de Livraison
        </Typography>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Scope actif: {scopeSummary}
      </Alert>

      {!selectedGroupId && (
        <ScopeRequirementAlert
          requiredScope="group"
          title="Sélection de groupe requise"
          description="Cette page gère les tarifs de livraison au niveau du groupe opérationnel."
        />
      )}

      {selectedGroupId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ces règles s’appliquent uniquement au groupe actif.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {selectedGroupId && (
        <>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Règles de Tarification de Livraison
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ces règles déterminent le prix de livraison appliqué en fonction de la distance
              pour le groupe opérationnel actif.
            </Typography>

            <Stack
              direction="column"
              spacing={1.5}
              sx={{ mb: 2 }}
              alignItems="flex-start"
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={enableAutoFreeDelivery}
                    onChange={(_, checked) => setEnableAutoFreeDelivery(checked)}
                  />
                }
                label="Activer la livraison gratuite automatique"
              />

              <TextField
                label="Montant minimum commande pour livraison gratuite"
                type="number"
                size="small"
                value={freeDeliveryMinOrderAmount ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setFreeDeliveryMinOrderAmount(v === '' ? undefined : Number(v));
                }}
                InputProps={{ inputProps: { min: 0, step: 1 } }}
                FormHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                helperText="En dessous de ce montant, les frais de livraison s'appliquent. À partir de ce montant, la livraison est offerte (si l'option est activée)."
                sx={{ maxWidth: 360, width: '100%' }}
                disabled={!enableAutoFreeDelivery}
              />

              <TextField
                label="Rayon max pour livraison gratuite (km)"
                type="number"
                size="small"
                value={maxFreeDeliveryKm ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setMaxFreeDeliveryKm(v === '' ? undefined : Number(v));
                }}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                FormHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                helperText="À l'intérieur de ce rayon ET au-dessus du montant minimum, la livraison est offerte. Au-delà, les frais normaux s'appliquent."
                sx={{ maxWidth: 260, width: '100%' }}
                disabled={!enableAutoFreeDelivery}
              />

              <TextField
                label="Rayon maximum de livraison (km)"
                type="number"
                size="small"
                value={maxClientDistanceKm ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setMaxClientDistanceKm(v === '' ? undefined : Number(v));
                }}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                FormHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                helperText="Au-delà de ce rayon, les commandes seront bloquées côté serveur et dans l'application client."
                sx={{ maxWidth: 260, width: '100%' }}
              />
            </Stack>

            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Distance Min (km)</TableCell>
                    <TableCell>Distance Max (km)</TableCell>
                    <TableCell>Frais (MAD)</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={rule.minKm}
                          onChange={(e) => handleRuleChange(index, 'minKm', parseFloat(e.target.value))}
                          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                          sx={{ width: '100px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={rule.maxKm}
                          onChange={(e) => handleRuleChange(index, 'maxKm', parseFloat(e.target.value))}
                          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                          sx={{ width: '100px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={rule.fee}
                          onChange={(e) => handleRuleChange(index, 'fee', parseFloat(e.target.value))}
                          InputProps={{ inputProps: { min: 0 } }}
                          sx={{ width: '100px' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="error"
                          onClick={() => deleteRule(index)}
                          disabled={rules.length <= 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addRule}
              >
                Ajouter une règle
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <CircularProgress size={24} /> : 'Enregistrer les règles'}
              </Button>
            </Stack>
          </Paper>

          <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Commission Groupe des Drivers
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Appliquer un pourcentage de commission identique à tous les drivers du groupe actif.
            </Typography>

            {percentageError && <Alert severity="error" sx={{ mb: 2 }}>{percentageError}</Alert>}
            {percentageSuccess && <Alert severity="success" sx={{ mb: 2 }}>{percentageSuccess}</Alert>}

            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Pourcentage (%)"
                type="number"
                size="small"
                value={globalPercentage}
                onChange={(e) => {
                  setGlobalPercentage(Number(e.target.value));
                  setPercentageSuccess(null);
                  setPercentageError(null);
                }}
                InputProps={{ inputProps: { min: 0, max: 100 } }}
                sx={{ width: 160 }}
              />
              <Button
                variant="contained"
                color="warning"
                disabled={applyingPercentage}
                onClick={async () => {
                  if (globalPercentage < 0 || globalPercentage > 100) {
                    setPercentageError('Le pourcentage doit être entre 0 et 100.');
                    return;
                  }
                  setApplyingPercentage(true);
                  setPercentageError(null);
                  setPercentageSuccess(null);
                  try {
                    const count = await driverService.applyGlobalPercentage(globalPercentage);
                    setPercentageSuccess(`Commission mise à jour à ${globalPercentage}% pour ${count} driver(s).`);
                  } catch (err: any) {
                    setPercentageError(`Erreur: ${err.message}`);
                  } finally {
                    setApplyingPercentage(false);
                  }
                }}
              >
                {applyingPercentage ? <CircularProgress size={24} /> : 'Appliquer aux drivers du groupe'}
              </Button>
            </Stack>
          </Paper>
        </>
      )}
    </>
  );

  return embedded ? content : (
    <Layout title="Tarifs de Livraison">
      <Container maxWidth="md">{content}</Container>
    </Layout>
  );
};

const DeliveryRulesConfigPage: React.FC = () => <DeliveryRulesSettingsPanel />;

export default DeliveryRulesConfigPage;
